import { ArrowLeft, Clock, Play } from 'lucide-react';
import { useT } from '../../i18n';

export interface CreateTournamentViewProps {
  /** Navigates back to the previous screen. */
  onBack: () => void;
  /** Opens the Padel24 tournament flow. */
  onOpenPadel24: () => void;
  /** Opens the club championship (Klubbmesterskap) flow. */
  onOpenClubChampionship: () => void;
  /** Opens the mini-tournaments (Miniturneringer) flow. */
  onOpenMiniTournaments: () => void;
  /** Opens the single-match (Enkeltkamp) flow. */
  onOpenSingleMatch: () => void;
  /** Opens the series play (Seriespill) flow. */
  onOpenSeries: () => void;
}

/** Tournament-type picker landing page; extracted verbatim from the original monolith (legacy/App.jsx). */
export function CreateTournamentView({
  onBack,
  onOpenPadel24,
  onOpenClubChampionship,
  onOpenMiniTournaments,
  onOpenSingleMatch,
  onOpenSeries,
}: CreateTournamentViewProps) {
  const t = useT();
  const categories = [
    {
      id: 'padel24',
      name: t('tournament.create.padel24.name'),
      tagline: t('tournament.create.padel24.tagline'),
      desc: t('tournament.create.padel24.desc'),
      features: [
        t('tournament.create.padel24.f1'),
        t('tournament.create.padel24.f2'),
        t('tournament.create.padel24.f3'),
        t('tournament.create.padel24.f4'),
      ],
      eloWeight: 100,
      gradient: 'linear-gradient(135deg, #ecfccb 0%, #d9f99d 60%, #bef264 100%)',
      textColor: '#1a2e05',
      labelColor: '#3f6212',
      active: true,
      onClick: onOpenPadel24,
    },
    {
      id: 'clubChampionship',
      name: t('tournament.create.km.name'),
      tagline: t('tournament.create.km.tagline'),
      desc: t('tournament.create.km.desc'),
      features: [
        t('tournament.create.km.f1'),
        t('tournament.create.km.f2'),
        t('tournament.create.km.f3'),
        t('tournament.create.km.f4'),
      ],
      eloWeight: 175,
      gradient: 'linear-gradient(135deg, #fdf2f8 0%, #fbcfe8 60%, #f9a8d4 100%)',
      textColor: '#500724',
      labelColor: '#9d174d',
      active: true,
      onClick: onOpenClubChampionship,
    },
    {
      id: 'series',
      name: t('tournament.create.league.name'),
      tagline: t('tournament.create.league.tagline'),
      desc: t('tournament.create.league.desc'),
      features: [
        t('tournament.create.league.f1'),
        t('tournament.create.league.f2'),
        t('tournament.create.league.f3'),
        t('tournament.create.league.f4'),
        t('tournament.create.league.f5'),
        t('tournament.create.league.f6'),
      ],
      eloWeight: 200,
      gradient: 'linear-gradient(135deg, #dbeafe 0%, #93c5fd 60%, #60a5fa 100%)',
      textColor: '#1e3a8a',
      labelColor: '#1d4ed8',
      active: true,
      onClick: onOpenSeries,
    },
    {
      id: 'miniTournaments',
      name: t('tournament.create.mini.name'),
      tagline: t('tournament.create.mini.tagline'),
      desc: t('tournament.create.mini.desc'),
      features: [
        t('tournament.create.mini.f1'),
        t('tournament.create.mini.f2'),
        t('tournament.create.mini.f3'),
        t('tournament.create.mini.f4'),
      ],
      eloWeight: 50,
      gradient: 'linear-gradient(135deg, #fef9c3 0%, #fde047 60%, #facc15 100%)',
      textColor: '#422006',
      labelColor: '#713f12',
      active: true,
      onClick: onOpenMiniTournaments,
    },
    {
      id: 'singleMatch',
      name: t('tournament.create.single.name'),
      tagline: t('tournament.create.single.tagline'),
      desc: t('tournament.create.single.desc'),
      features: [
        t('tournament.create.single.f1'),
        t('tournament.create.single.f2'),
        t('tournament.create.single.f3'),
        t('tournament.create.single.f4'),
      ],
      eloWeight: 100,
      eloWeightLabel: '0–200', // ELO weight is chosen per match — show the range on the card
      gradient: 'linear-gradient(135deg, #ffedd5 0%, #fb923c 60%, #ea580c 100%)',
      textColor: '#431407',
      labelColor: '#9a3412',
      active: true,
      onClick: onOpenSingleMatch,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-10 slide-up">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
        >
          <ArrowLeft size={12} /> {t('tournament.create.back')}
        </button>
        <h1 className="font-display text-3xl font-semibold mb-1">{t('tournament.create.title')}</h1>
        <p className="text-sm text-ink-3">{t('tournament.create.lead')}</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {categories.map((cat) => {
          const isClickable = cat.active && cat.onClick;

          // Inactive cards: grey "coming soon" style (same as disabled mini-cards)
          if (!isClickable) {
            return (
              <div key={cat.id} className="card p-6 opacity-60 cursor-not-allowed">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2">
                    <Clock size={12} strokeWidth={2.5} className="text-ink-3" />
                    <span className="font-mono text-[9px] font-bold tracking-[0.18em] uppercase text-ink-3">
                      {t('tournament.create.comingSoon')}
                    </span>
                  </div>
                  {/* ELO weight — shown as a pill in the top right. The user sees
                      at a glance how much the format counts toward ranking. */}
                  <span
                    className="font-mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-ink-3"
                    style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)' }}
                    title={t('tournament.create.eloWeightTitle', {
                      weight: cat.eloWeightLabel ? cat.eloWeightLabel + '%' : cat.eloWeight + '%',
                    })}
                  >
                    {t('tournament.create.eloWeightPill', {
                      weight: cat.eloWeightLabel ? cat.eloWeightLabel + '%' : cat.eloWeight + '%',
                    })}
                  </span>
                </div>
                <h3 className="font-display text-xl font-semibold mb-0.5 leading-tight text-ink-2">
                  {cat.name}
                </h3>
                <div className="font-mono text-[10px] mb-2 uppercase tracking-wider text-ink-3">
                  {cat.tagline}
                </div>
                <p className="text-xs leading-relaxed mb-3 text-ink-3">{cat.desc}</p>
                <ul className="space-y-1">
                  {cat.features.map((f, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-1.5 text-[11px] leading-snug text-ink-3"
                    >
                      <span className="flex-shrink-0 mt-0.5">·</span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }

          // Active cards: colourful gradient
          return (
            <button
              key={cat.id}
              onClick={cat.onClick}
              className="card-elev p-6 text-left transition-all hover:shadow-lg hover:-translate-y-0.5 cursor-pointer"
              style={{ background: cat.gradient }}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="flex items-center gap-2">
                  <Play size={12} strokeWidth={2.5} style={{ color: cat.labelColor }} />
                  <span
                    className="font-mono text-[9px] font-bold tracking-[0.18em] uppercase"
                    style={{ color: cat.labelColor }}
                  >
                    {t('tournament.create.available')}
                  </span>
                </div>
                {/* ELO weight — shows how much this format affects ELO */}
                <span
                  className="font-mono text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{
                    color: cat.labelColor,
                    background: 'rgba(255,255,255,0.4)',
                    border: `1px solid ${cat.labelColor}33`,
                  }}
                  title={t('tournament.create.eloWeightTitle', {
                    weight: cat.eloWeightLabel ? cat.eloWeightLabel + '%' : cat.eloWeight + '%',
                  })}
                >
                  {t('tournament.create.eloWeightPill', {
                    weight: cat.eloWeightLabel ? cat.eloWeightLabel + '%' : cat.eloWeight + '%',
                  })}
                </span>
              </div>
              <h3
                className="font-display text-xl font-semibold mb-0.5 leading-tight"
                style={{ color: cat.textColor }}
              >
                {cat.name}
              </h3>
              <div
                className="font-mono text-[10px] mb-2 uppercase tracking-wider"
                style={{ color: cat.labelColor }}
              >
                {cat.tagline}
              </div>
              <p className="text-xs leading-relaxed mb-3" style={{ color: cat.labelColor }}>
                {cat.desc}
              </p>
              <ul className="space-y-1">
                {cat.features.map((f, i) => (
                  <li
                    key={i}
                    className="flex items-start gap-1.5 text-[11px] leading-snug"
                    style={{ color: cat.labelColor }}
                  >
                    <span className="flex-shrink-0 mt-0.5">·</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </button>
          );
        })}
      </div>
    </div>
  );
}
